import React, { useEffect, useRef, useState } from "react";
import BaseDatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import { useController, Control, FieldErrors, RegisterOptions } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { Star, Check, ChevronsUpDown, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import "react-datepicker/dist/react-datepicker.css";

import { enGB } from "date-fns/locale";

registerLocale("en-GB", enGB);
setDefaultLocale("en-GB");

const FieldError = ({ errors, name }: { errors: FieldErrors; name: string }) => (
  <ErrorMessage
    errors={errors}
    name={name}
    render={({ message }) => <p className="mt-1 text-sm text-red-600">{message}</p>}
  />
);

interface HookImageInputProps {
  control: Control<any>;
  name: string;
  defaultValue?: any;
  errors: FieldErrors;
}

export const HookImageInput = ({ control, name, ...props }: HookImageInputProps) => {
  const {
    field: { ref, ...inputProps },
  } = useController({
    name,
    control,
    rules: { required: true },
    defaultValue: props.defaultValue,
  });
  const img = inputProps.value;
  let photo_url = img;

  if (typeof img === "object") {
    photo_url = URL.createObjectURL(img);
    URL.revokeObjectURL(img[0]);
  }

  delete (inputProps as any).value;
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const imageFile = event.target.files?.[0];
    if (imageFile) {
      const localImageUrl = URL.createObjectURL(imageFile);
      const imageObject = new window.Image();
      imageObject.onload = () => {
        (imageFile as any).width = imageObject.naturalWidth;
        (imageFile as any).height = imageObject.naturalHeight;
        inputProps.onChange(imageFile);
        URL.revokeObjectURL(imageFile as any);
      };
      imageObject.src = localImageUrl;
    }
  };
  return (
    <>
      <div className="relative inline-block">
        <label htmlFor={name}>
          <img
            src={photo_url}
            alt=""
            className="h-36 w-36 cursor-pointer rounded object-cover"
          />
        </label>
        <input
          type="file"
          {...inputProps}
          onChange={(event) => handleChange(event)}
          accept=".jpg,.jpeg,.gif,.png,.heic"
          id={name}
          className="sr-only"
        />
      </div>
      <FieldError errors={props.errors} name={name} />
    </>
  );
};

interface HookInputProps {
  control: Control<any>;
  name: string;
  rules?: RegisterOptions;
  defaultValue?: any;
  errors: FieldErrors;
  label?: string;
  inline?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  type?: string;
  rightLabel?: string;
  errorMessage?: string | false;
  fieldRef?: React.RefObject<any>;
  nextFieldRef?: React.RefObject<any>;
  keyboardType?: string;
  submitOnReturn?: any;
}

export const HookInput = ({ control, name, rules, ...props }: HookInputProps) => {
  const {
    field: { ref, ...inputProps },
    fieldState: { isTouched },
  } = useController({
    name,
    control,
    rules: rules || { required: true },
    defaultValue: props.defaultValue,
  });

  const hasError = props.errors[name] && isTouched;
  const fieldError = props.errors[name] ? (props.errors[name] as any).message : false;

  return (
    <div className={cn("mb-3", props.inline && "inline-block mr-3")}>
      <Label htmlFor={name} className="mb-1 block">
        {props.label}
      </Label>
      {props.multiline ? (
        <Textarea
          {...inputProps}
          id={name}
          rows={props.numberOfLines}
          className={cn(hasError && "border-red-500")}
        />
      ) : (
        <div className="flex items-center gap-2">
          <Input
            {...inputProps}
            id={name}
            type={props.type}
            className={cn(hasError && "border-red-500")}
          />
          {props.rightLabel && (
            <Badge variant="outline">{props.rightLabel}</Badge>
          )}
        </div>
      )}
      {fieldError && <p className="mt-1 text-sm text-red-600">{fieldError}</p>}
    </div>
  );
};

interface DatePickerProps {
  control: Control<any>;
  name: string;
  label: string;
}

export const DatePicker = ({ control, name, label }: DatePickerProps) => {
  const {
    field: { ref, ...inputProps },
  } = useController({
    name,
    control,
    rules: { required: true },
  });

  return (
    <div className="mb-3">
      <Label htmlFor={name} className="mb-1 block">
        {label}
      </Label>
      <BaseDatePicker
        locale="en-GB"
        dateFormat="dd MMM yyyy"
        selected={!inputProps.value ? null : new Date(inputProps.value)}
        onChange={inputProps.onChange}
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      />
    </div>
  );
};

interface DropdownOption {
  key?: string;
  value: string;
  text: string;
  content?: React.ReactNode;
}

interface HookDropdownProps {
  control: Control<any>;
  name: string;
  label: string;
  errors: FieldErrors;
  options: DropdownOption[];
  placeholder?: string;
  search?: boolean;
  clearable?: boolean;
  defaultValue?: any;
  labelStyle?: React.CSSProperties;
  selectStyle?: React.CSSProperties;
}

export const HookDropdown = ({
  control,
  name,
  label,
  errors,
  options,
  placeholder,
  search = false,
  ...props
}: HookDropdownProps) => {
  const {
    field: { ref, ...inputProps },
    fieldState: { isTouched },
  } = useController({
    name,
    control,
    rules: { required: true },
    defaultValue: props.defaultValue,
  });
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const hasError = errors[name] && isTouched;
  const selectedOption = options.find((opt) => opt.value === inputProps.value);

  // cmdk auto-scrolls its highlighted item into view on every keystroke but
  // never resets the list scroll, so changing the query can leave the list
  // stranded partway down. Reset to the top whenever the search term changes
  // (passive effect, so it runs after cmdk's layout-effect scroll).
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [searchTerm]);

  return (
    <div className={cn("mb-3", hasError && "text-red-600")}>
      <Label style={props.labelStyle} className="mb-1 block">
        {label}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs",
              hasError && "border-red-500",
            )}
            style={props.selectStyle}
          >
            <span className="truncate">
              {selectedOption ? selectedOption.text : placeholder || "Select..."}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command
            filter={(value, search) =>
              value.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0
            }
          >
            {search && (
              <CommandInput placeholder="Search..." onValueChange={setSearchTerm} />
            )}
            <CommandList ref={listRef}>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt.key || opt.value}
                    value={opt.text}
                    onSelect={() => {
                      inputProps.onChange(
                        opt.value === inputProps.value ? "" : opt.value,
                      );
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        inputProps.value === opt.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {opt.content || opt.text}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FieldError errors={errors} name={name} />
    </div>
  );
};

interface SelectItem {
  label: string;
  value: string;
}

interface HookSelectProps {
  control: Control<any>;
  name: string;
  label: string;
  items: SelectItem[];
  errors: FieldErrors;
  defaultValue?: any;
  hideLabel?: boolean;
  labelStyle?: React.CSSProperties;
  selectStyle?: React.CSSProperties;
  errorMessage?: string | false;
  fieldRef?: React.RefObject<any>;
}

export const HookSelect = ({ control, name, label, items, errors, ...props }: HookSelectProps) => {
  const {
    field: { ref, ...inputProps },
    fieldState: { isTouched },
  } = useController({
    name,
    control,
    rules: { required: true },
    defaultValue: props.defaultValue,
  });

  const hasError = errors[name] && isTouched;
  return (
    <div className={cn("mb-3", hasError && "text-red-600")}>
      {!props.hideLabel && (
        <Label style={props.labelStyle} className="mb-1 block">
          {label}
        </Label>
      )}
      <select
        {...inputProps}
        style={props.selectStyle}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          hasError && "border-red-500",
        )}
      >
        {items.map(({ label, value }) => (
          <option value={value} key={value}>
            {label}
          </option>
        ))}
      </select>
      <FieldError errors={errors} name={name} />
    </div>
  );
};

interface HookStarsProps {
  control: Control<any>;
  name: string;
  label: string;
  errors: FieldErrors;
  defaultValue?: any;
  fieldRef?: React.RefObject<any>;
}

export const HookStars = ({ control, name, label, errors, ...props }: HookStarsProps) => {
  const {
    field: { ref, onChange, ...inputProps },
    fieldState: { isTouched },
  } = useController({
    name,
    control,
    rules: { required: true },
    defaultValue: props.defaultValue,
  });

  const hasError = errors[name] && isTouched;
  const rating = inputProps.value || 0;

  return (
    <div className={cn("mb-3", hasError && "text-red-600")}>
      <Label className="mb-1 block">{label}</Label>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="p-0.5 hover:scale-110 transition-transform"
          >
            <Star
              className={cn(
                "h-5 w-5",
                star <= rating
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-gray-300",
              )}
            />
          </button>
        ))}
      </div>
      <FieldError errors={errors} name={name} />
    </div>
  );
};

interface HookTagInputProps {
  control: Control<any>;
  name: string;
  label: string;
  errors: FieldErrors;
  defaultValue?: string[];
}

export const HookTagInput = ({ control, name, label, errors, ...props }: HookTagInputProps) => {
  const [inputValue, setInputValue] = useState("");
  const {
    field: { ref, ...inputProps },
  } = useController({
    name,
    control,
    rules: { required: false },
    defaultValue: props.defaultValue || [],
  });

  const tags: string[] = inputProps.value || [];

  const addTag = (text: string) => {
    const trimmed = text.trim();
    if (trimmed && !tags.includes(trimmed)) {
      inputProps.onChange([...tags, trimmed]);
    }
    setInputValue("");
  };

  const removeTag = (index: number) => {
    inputProps.onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      e.preventDefault();
      const lastTag = tags[tags.length - 1];
      inputProps.onChange(tags.slice(0, -1));
      setInputValue(lastTag);
    }
  };

  return (
    <div className="mb-3">
      <Label htmlFor={name} className="mb-1 block">
        {label}
      </Label>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {tags.map((tag, index) => (
            <Badge key={index} variant="secondary" className="gap-1">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(index)}
                className="ml-0.5 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Input
        id={name}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => addTag(inputValue)}
        placeholder="Type and press Enter to add"
      />
      <FieldError errors={errors} name={name} />
    </div>
  );
};

interface CheckBoxProps {
  control: Control<any>;
  name: string;
  label: string;
  errors?: FieldErrors;
  defaultValue?: any;
}

export const CheckBox = ({ control, name, label }: CheckBoxProps) => {
  const {
    field: { ref, ...inputProps },
  } = useController({
    name,
    control,
    rules: { required: false },
  });
  return (
    <div className="mb-3 flex items-center gap-2">
      <Checkbox
        id={name}
        checked={inputProps.value}
        onCheckedChange={(checked) => inputProps.onChange(checked)}
      />
      <Label htmlFor={name}>{label}</Label>
    </div>
  );
};
